/**
 * S-22 announcer bridge (V4) — turns SM-5 WS pushes into design-system
 * announcer stings. Fire-and-forget cosmetics: a missed overlay is no loss
 * (screens.md S-22); the REST view is the recovery plane.
 *
 * Honesty rule (SM-5 / copy §1.5): a token grant swallowed by the hold-1 /
 * bank-1 caps announces the loss in the sub-line — no silent swallowing.
 */
import { useEffect } from "react";
import { formatCopy, resolveCopy } from "@humble/shared";
import { announce } from "../../design/index.js";
import { createStore } from "../../lib/store.js";
import { ws } from "../../lib/ws.js";

/** Last-known streak count (streak.broken carries no payload; ©announcer.broken wants {n}). */
export const streakStore = createStore<{ count: number; tierReached: number }>({
  count: 0,
  tierReached: 0,
});

let bridged = false;

/** Idempotent global wiring — mounted once by any killstreak-aware screen. */
export function ensureKillstreakAnnouncer(): void {
  if (bridged) return;
  bridged = true;

  ws.on("streak.updated", ({ count, tier_reached }) => {
    streakStore.set({ count, tierReached: tier_reached ?? 0 });
  });

  ws.on("streak.tier", ({ tier, grant }) => {
    const sting =
      tier === "beyond"
        ? (resolveCopy("announcer.beyond") ?? "BEYOND GODLIKE.")
        : (resolveCopy(`announcer.tiers.${tier}`) ?? `STREAK TIER ${tier}`);
    const sub = grant?.lost
      ? "Token cap reached — this grant is LOST. Hold 1, bank 1. House rules."
      : undefined;
    // v3 — the plate's combo meter ticks up with the ladder (5 ticks = beyond)
    const combo =
      tier === "beyond" ? 5 : ({ 3: 1, 5: 2, 7: 3, 10: 4 } as Record<number, number>)[tier] ?? 0;
    announce({ sting, sub, motif: "gold", combo });
  });

  ws.on("streak.broken", () => {
    const n = streakStore.get().count;
    streakStore.set({ count: 0, tierReached: 0 });
    if (n <= 0) return;
    const template = resolveCopy("announcer.broken") ?? "Streak ended at {n}.";
    announce({ sting: "STREAK BROKEN", sub: formatCopy(template, { n }), motif: "gold" });
  });
}

export function useKillstreakAnnouncer(): void {
  useEffect(() => {
    ensureKillstreakAnnouncer();
    ws.ensureConnected();
  }, []);
}
